/**
 * How a saved database connection is described in the pickers.
 *
 * Two saved connections can share a type, host and database and still be different sources: the store's own identity
 * is (db_type, host, port, database, schema_name, username), and the default name is just "source DB (oracle)". The
 * pickers used to show only name + type + host/database, so two entries differing in schema or user looked identical.
 */

export interface SavedConnectionLike {
  name?: string | null;
  db_type?: string | null;
  host?: string | null;
  port?: number | null;
  database?: string | null;
  schema_name?: string | null;
  username?: string | null;
  service_name?: string | null;
  auth_type?: string | null;
  account?: string | null;
  warehouse?: string | null;
  role?: string | null;
  http_path?: string | null;
  bridge_label?: string | null;
  updated_at?: string | null;
}

// create_project_db names an unnamed connection exactly "<kind> DB (<db_type>)" (kind source|target, db_type a lowercase
// identifier), which says nothing about which one it is. Deliberately NOT case-insensitive and not `.*`: a name a user
// typed ("Source DB (Prod)") must be kept as typed.
const AUTO_NAME = /^(source|target) DB \([a-z0-9_]*\)$/;

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/** The first line: the user's own name for it, or else who/what it connects as. */
export function savedConnectionTitle(c: SavedConnectionLike): string {
  const name = (c.name ?? "").trim();
  if (name && !AUTO_NAME.test(name)) return name;
  const target = c.database || c.service_name || c.host || "";
  if (c.username && target) return `${c.username}@${target}`;
  return name || c.db_type || "connection";
}

/** The second line: the endpoint, as before. */
export function savedConnectionEndpoint(c: SavedConnectionLike): string {
  const where = c.host || c.bridge_label;
  const host = where ? ` · ${where}` : "";
  // Shown whenever it is set: hiding "default" ports by number alone is wrong across types (Postgres on 1521).
  const port = c.port ? `:${c.port}` : "";
  const db = c.database ? `/${c.database}` : "";
  return `${c.db_type ?? ""}${host}${port}${db}`;
}

/** The third line: what tells two entries on the same endpoint apart. Empty parts are left out. */
export function savedConnectionDetail(c: SavedConnectionLike): string {
  const parts: string[] = [];
  if (c.username) parts.push(`user ${c.username}`);
  if (c.schema_name) parts.push(`schema ${c.schema_name}`);
  if (c.service_name && c.service_name !== c.database)
    parts.push(`service ${c.service_name}`);
  // Identity that lives outside host/user/schema for the warehouse-style connectors.
  if (c.account) parts.push(`account ${c.account}`);
  if (c.warehouse) parts.push(`warehouse ${c.warehouse}`);
  if (c.role) parts.push(`role ${c.role}`);
  if (c.http_path) parts.push(`path ${c.http_path.split("/").pop()}`);
  if (c.auth_type && c.auth_type !== "password") parts.push(c.auth_type);
  const updated = c.updated_at ? formatDate(c.updated_at) : "";
  if (updated) parts.push(`updated ${updated}`);
  return parts.join(" · ");
}
